import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type Ref,
} from "react";
import type { SparkController } from "../hooks/useSpark.js";
import styles from "./HoldButton.module.css";

interface HoldButtonProps {
  spark: SparkController;
  /** Fired when the visitor lets go of a fully charged button. */
  onLaunch: () => void;
  disabled?: boolean;
  busyLabel?: string;
  /** The button itself, for effects drawn around it. */
  buttonRef?: Ref<HTMLButtonElement>;
}

/**
 * Press-and-hold trigger. The fill tracks the shared spark charge; letting go early
 * lets it drain back, letting go when full launches. Huge and half off screen, so it is
 * hard to miss with a thumb.
 */
export function HoldButton({
  spark,
  onLaunch,
  disabled = false,
  busyLabel,
  buttonRef,
}: HoldButtonProps) {
  const fillRef = useRef<HTMLSpanElement>(null);
  const [full, setFull] = useState(false);

  useEffect(
    () =>
      spark.onFrame((state) => {
        const fill = fillRef.current;
        // 50% of a square's clip reference box is its half-width: full charge = full circle.
        if (fill) fill.style.clipPath = `circle(${state.charge * 50}% at 50% 50%)`;
        setFull(state.charge >= 1);
      }),
    [spark],
  );

  function start(): void {
    if (!disabled) spark.setHolding(true);
  }

  function end(commit: boolean): void {
    if (!spark.state.holding) return;
    spark.setHolding(false);
    if (commit && spark.state.charge >= 1) onLaunch();
  }

  function onPointerDown(event: PointerEvent<HTMLButtonElement>): void {
    event.currentTarget.setPointerCapture(event.pointerId);
    start();
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>): void {
    if (event.key !== " " && event.key !== "Enter") return;
    event.preventDefault();
    if (!event.repeat) start();
  }

  function onKeyUp(event: KeyboardEvent<HTMLButtonElement>): void {
    if (event.key !== " " && event.key !== "Enter") return;
    event.preventDefault();
    end(true);
  }

  const label = disabled && busyLabel ? busyLabel : full ? "Release" : "Press and hold";

  return (
    <button
      ref={buttonRef}
      type="button"
      aria-label={label}
      className={styles.button}
      disabled={disabled}
      onPointerDown={onPointerDown}
      onPointerUp={() => end(true)}
      onPointerCancel={() => end(false)}
      onLostPointerCapture={() => end(false)}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      onBlur={() => end(false)}
      onContextMenu={(event) => event.preventDefault()}
    >
      <span ref={fillRef} className={styles.fill} aria-hidden="true" />
      <span className={styles.label} aria-hidden="true">
        {label}
      </span>
    </button>
  );
}
